/**
 * MaintenanceGuard - checks maintenance mode and redirects non-admins
 * 
 * Uses try-catch wrapper for useUserRole to handle edge cases during HMR
 * when QueryClient context might not be fully initialized.
 */

import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUserRole } from '@/hooks/useUserRole';
import { featureFlagsService } from '@/lib/services/featureFlagsService';

interface MaintenanceGuardProps {
  children: React.ReactNode;
}

export function MaintenanceGuard({ children }: MaintenanceGuardProps) {
  const location = useLocation();
  const [maintenanceMode, setMaintenanceMode] = useState<boolean | null>(null);
  
  // Hook must always be called at top level - never inside try/catch
  const { isAdmin } = useUserRole();

  useEffect(() => {
    const checkMaintenance = async () => {
      const enabled = await featureFlagsService.isEnabled('app_maintenance_mode');
      setMaintenanceMode(enabled);
    };
    checkMaintenance();
    
    // Refresh every 30 seconds to catch maintenance mode changes
    const interval = setInterval(checkMaintenance, 30000);
    return () => clearInterval(interval);
  }, []);

  // Allow maintenance page, admin routes, and auth routes (for admins to log in)
  const isMaintenancePage = location.pathname === '/maintenance';
  const isAdminRoute = location.pathname === '/drugadm';
  const isAuthRoute = location.pathname.startsWith('/auth') || 
                      location.pathname === '/login' || 
                      location.pathname === '/register';

  // Still loading
  if (maintenanceMode === null) {
    return <>{children}</>;
  }

  // Allow admin route and auth routes even during maintenance (so admins can log in)
  if (isMaintenancePage || isAdminRoute || isAuthRoute) {
    return <>{children}</>;
  }

  // Maintenance mode enabled and user is not admin
  if (maintenanceMode && !isAdmin) {
    return <Navigate to="/maintenance" replace />;
  }

  return <>{children}</>;
}

export default MaintenanceGuard;
